import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { pairwise } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ValidationService } from './validation.service';

@Injectable({ providedIn: 'root' })
export class FormService {

  constructor(private validationService: ValidationService) {}

  initControlWithDefaultValueAndSetRegExBasedOnValueToAnother(values: FormDefaultValueAndSetRegExModel): void {
    const controlDefaultValue = values.controlForDefaultValue;
    const controlRegEx = values.controlToSetRegEx;

    if (!controlDefaultValue || !controlRegEx) {
      throw new Error('Form controls not found.');
    }

    controlDefaultValue
    .valueChanges
    .pipe(takeUntilDestroyed(values.destroyRef), startWith(controlDefaultValue.value), pairwise())
    .subscribe(([previous, current]) => {
      this.setInitialDefaultValue(previous, current, controlDefaultValue, values.defaultValue);
      this.updateRegExValidatorDynamically(controlRegEx, values.metadataKey, controlDefaultValue, values.metadataRegExKey);
    });
  }

  private setInitialDefaultValue(previous: any, current: any, controlDefaultValue: AbstractControl<any, any>, defaultValue: string) {
    if (!previous && !current) {
      if (!controlDefaultValue) {
        throw new Error('Form control not found.');
      }
      if (!controlDefaultValue.value) {
        controlDefaultValue.setValue(defaultValue, { emitEvent: false });
        controlDefaultValue.updateValueAndValidity();
      }
    }
  }

  private updateRegExValidatorDynamically(controlRegEx: AbstractControl<any, any>, metadataKey: string, controlDefaultValue: AbstractControl<any, any>, metadataRegExKey: string) {
    controlRegEx.setValidators([
      ...this.validationService.getValidators(metadataKey),
      this.regExValidator(controlDefaultValue as AbstractControl, this.validationService.getProperties(metadataRegExKey)),
    ]);
    controlRegEx.updateValueAndValidity();
  }

  private regExValidator(controlDefaultValue: AbstractControl, common: { [x: string]: any; }): ValidatorFn {

    return (controlRegEx: AbstractControl): ValidationErrors | null => {
      const currentValue = controlDefaultValue.value ?? '';
      const valueToTest = (controlRegEx.value ?? '').toString().trim();

      if (!valueToTest) return null;

      const regEx = common[currentValue] ? common[currentValue] : null;
      if (regEx) {
        return new RegExp(regEx).test(valueToTest) ? null : { failed: true };
      }

      return null;
    }
  }
}

export interface FormDefaultValueAndSetRegExModel {
  controlForDefaultValue: AbstractControl;
  defaultValue: string;
  controlToSetRegEx: AbstractControl;
  metadataKey: string;
  metadataRegExKey: string;
  destroyRef: DestroyRef;
}
